import { useLocation, useNavigate, useRevalidator } from '@remix-run/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled, { withTheme } from 'styled-components';
import { CUSTOM_EVENTS } from '~/common/constants/events';
import { Routes } from '~/common/constants/routes';
import {
  CAMPAIGN_PATHS,
  CAMPAIGN_GATE_PATHS,
  CAMPAIGN_SIGN_UP_PATHS,
  earlyAccessPathRegion,
} from '~/common/constants/gatedRoutes';
import { useDevice } from '~/common/hooks/useDevice';
import useMediaQuery from '~/common/hooks/useMediaQuery';
import { CookieService } from '~/common/utils/cookies';
import { isBrowser } from '~/common/utils/browser';
import { useCartSSR } from '~/recoil/useCartSSR';
import { Theme } from '~/theme';
import Header from './components/header';
import RollingHeader from './components/header/RollingHeader';
import { Scrollbar } from './components/scrollbar';
import Sidebar from './components/sidebar';
import { Root } from './styles';
import useColor from '~/recoil/useColor';
import { regionModalOpenState, useRegion } from '~/recoil/useRegion';
import { useSetRecoilState } from 'recoil';
import { REGIONS, STORES, type Store } from '~/common/constants/region';
import CookiesBanner from './components/CookiesBanner';
import CookiePreferencesPanel from './components/CookiePreferencesPanel';
import RegionSelectModal from '~/components/RegionSelectModal';
import {
  useEuEarlyAccess,
  useRegionsEnabled,
} from '~/common/hooks/useRegionsEnabled';

interface LayoutProps {
  children: JSX.Element | JSX.Element[];
  showLogo?: boolean;
  theme?: Theme;
}

// Exact paths that stay reachable while a flash sale forces everything else home.
// `/private-sale-sign-up` is a paid-ad destination, so a campaign running during a
// drop would otherwise bounce every click and read as zero conversion. `/privacy` is
// linked from consent copy, and a policy link that redirects home is worse than
// none; `/terms` and `/cookie` have the same problem and are not fixed here.
//
// This list is the wrong shape — the closed set is what a flash sale REPLACES
// (shop, product, cart, checkout), so every new route is opted out by default.
// Inverting it is a filed follow-up; `/secret-event` is still broken today.
const FLASH_SALE_ALLOWED_PATHS = [
  '/',
  Routes.Sizing,
  // The legal pages stay reachable during a flash sale. `/cookie` especially: it is
  // where consent is withdrawn, and redirecting it to `/` would make withdrawal
  // impossible for as long as the sale ran.
  Routes.Privacy,
  Routes.Terms,
  Routes.Cookie,
  ...CAMPAIGN_GATE_PATHS,
  ...CAMPAIGN_SIGN_UP_PATHS,
];

// Routes that set their own theme colour, so the layout must not reset it.
const COLOR_OWNING_PATHS = [
  Routes.Product,
  Routes.Shop,
  ...CAMPAIGN_GATE_PATHS,
];

// The trailing slash is load-bearing: unbounded, this also matches the unrelated
// `/private-sale-sign-up`. Bounded, it covers `/<gate>/{handle}` only — which is why
// the bare gate paths are in the list above.
const isFlashSaleAllowed = (pathname: string): boolean =>
  FLASH_SALE_ALLOWED_PATHS.includes(pathname) ||
  CAMPAIGN_GATE_PATHS.some((path) => pathname.startsWith(`${path}/`));

const ownsThemeColor = (pathname: string): boolean =>
  COLOR_OWNING_PATHS.some((path) => pathname.includes(path));

const Layout = ({ children, showLogo = true }: LayoutProps) => {
  const [{ count }, updateCart] = useCartSSR();
  const { pathname } = useLocation();
  const { isTouchDevice } = useDevice();
  const navigate = useNavigate();
  const revalidator = useRevalidator();

  const isFlashSale =
    isBrowser && window?.ENV.PUBLIC_ENABLE_FLASH_SALE === 'true';

  const regionsEnabled = useRegionsEnabled();

  const [isMobileNavigationOpen, toggleMobileNavigation] =
    useState<boolean>(false);
  const [showCookiePreferences, setShowCookiePreferences] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const open = () => setShowCookiePreferences(true);

    window.addEventListener(CUSTOM_EVENTS.openCookiePreferences, open);
    return () =>
      window.removeEventListener(CUSTOM_EVENTS.openCookiePreferences, open);
  }, []);

  const { isTablet } = useMediaQuery();

  useEffect(() => {
    // Force navigation back to the homepage if flashsale is enabled. `pathname`
    // is a dependency so this also holds on client-side navigation, not just on
    // the first render.
    if (isFlashSale && !isFlashSaleAllowed(pathname)) {
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFlashSale, pathname]);

  const { region, setRegion } = useRegion();

  const euEarlyAccess = useEuEarlyAccess();

  const handleStoreChange = useCallback(
    (store: Store) => {
      if (!regionsEnabled) return;

      setRegion(store);
      // Pricing context is per region, so UK ↔ Rest of World keeps the bag; root
      // still re-runs, since the rolling header is per store.
      if (store.region !== region) updateCart({ items: { lineItems: [] } });
      revalidator.revalidate();
    },
    [regionsEnabled, region, setRegion, updateCart, revalidator],
  );

  const setRegionModalOpen = useSetRecoilState(regionModalOpenState);

  // SSR value on purpose: a document that loads WITH access was not just unlocked.
  const hadEarlyAccess = useRef(euEarlyAccess);

  useEffect(() => {
    if (!euEarlyAccess) {
      hadEarlyAccess.current = false;
      return;
    }
    const justUnlocked = !hadEarlyAccess.current;
    hadEarlyAccess.current = true;

    // Ask for the store on unlock. Only on the launch pages, where the picker
    // renders, so the open state cannot linger unseen and suppress the newsletter
    // pop-up elsewhere.
    if (justUnlocked && pathname.startsWith(CAMPAIGN_PATHS.euLaunch.gate))
      setRegionModalOpen(true);

    // Early access: the public shopping pages are UK; the launch pages and the rest
    // keep the shopper's store (see `earlyAccessPathRegion`). The server prices by the
    // same rule; this keeps the cookie, which decides /cart and checkout, in step.
    // Read the cookie, not `region`: the state is still the default on the first
    // effect pass and would clear the bag on every load.
    if (earlyAccessPathRegion(pathname) !== REGIONS.UK) return;
    if (CookieService.getRegionInfo()?.region !== REGIONS.UK) {
      setRegion(STORES.UK);
      // One checkout is one market, so the other market's bag cannot come along —
      // same as a manual region switch.
      updateCart({ items: { lineItems: [] } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [euEarlyAccess, pathname]);

  const { setColor } = useColor();

  useEffect(() => {
    if (!ownsThemeColor(pathname)) {
      setColor('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const ConditionalLayout = useCallback(() => {
    if (isFlashSale) {
      return <>{children}</>;
    }

    return (
      <StyledMain>
        <Sidebar activeTab={pathname} />
        {children}
      </StyledMain>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFlashSale, pathname]);

  return (
    <Root ref={rootRef}>
      <Header
        cartItemCount={count}
        showLogo={showLogo}
        currentRegion={region}
        isMobileNavigationOpen={isMobileNavigationOpen}
        toggleMobileNavigation={toggleMobileNavigation}
      />
      <RollingHeader />
      {ConditionalLayout()}
      {isTouchDevice || isTablet ? null : <Scrollbar rootRef={rootRef} />}
      {regionsEnabled && (
        <RegionSelectModal onStoreChange={handleStoreChange} />
      )}
      <CookiesBanner />
      <CookiePreferencesPanel
        isOpen={showCookiePreferences}
        onClose={() => setShowCookiePreferences(false)}
        onSave={(state) => {
          CookieService.setConsent({
            ...state,
            ts: new Date().toISOString(),
          });
          window.dispatchEvent(new Event(CUSTOM_EVENTS.cookieConsentUpdated));
          setShowCookiePreferences(false);
        }}
      />
    </Root>
  );
};

export const StyledMain = styled.main`
  display: flex;
  flex-direction: row;
  /* Root pads by the rolling header's strip; without this every page scrolls 2rem. */
  min-height: calc(100svh - var(--rolling-header-offset, 0px));

  /*
   * Every route renders as a flex item of this element, and a flex item's default
   * min-width: auto means it cannot shrink below its content's min-content width. A page
   * whose content is intrinsically wide -- a product grid whose tracks floor at the
   * intrinsic width of an <img> -- therefore stretches this row past the viewport and
   * scrolls the whole document sideways, at every width, on every such page.
   *
   * Set here rather than on each page wrapper: a new wrapper between StyledMain and a
   * route (CamoBackdrop was one) otherwise silently reintroduces it. The sidebar is
   * unaffected -- its visible layer is position: fixed.
   */
  & > * {
    min-width: 0;
  }
`;

export default withTheme(Layout);
